// Duplicates `getRevenueBreakdownMetrics`, `getTotalBusinessRevenueMetrics`,
// and `getGARevenueMetrics` from `apps/ui-rtk/src/constants/charts.tsx:84-229`.
// Signatures differ: this version takes a `ResolvedKpiPrefs` object instead of
// loose primitives, so the UI cannot import these directly without an adapter.
// Keep both copies aligned until the UI is refactored to use `~/shared/kpi`.

import type { MetricDefinition, ResolvedKpiPrefs } from './types';

export function getRevenueBreakdownMetrics(prefs: ResolvedKpiPrefs): MetricDefinition[] {
  const { depVar, amazonEnabled } = prefs;
  const metrics: MetricDefinition[] = [];

  if (depVar?.includes('ga_') || depVar?.includes('google')) {
    metrics.push({
      key: 'metrics.ga_baseline_revenue',
      label: 'Baseline Revenue',
      type: 'currency',
    });
    metrics.push({
      key: 'metrics.ga_revenue_baseline_plus_paid_brand_search',
      label: 'Baseline + Paid Brand Search Revenue',
      type: 'currency',
    });
    if (amazonEnabled) {
      metrics.push({
        key: 'metrics.ga_amazon_baseline_revenue_plus_paid_brand_search',
        label: 'Baseline + Paid Brand Search Revenue + Amazon Owned Revenue',
        type: 'currency',
      });
    }
  } else if (depVar?.includes('shopify')) {
    metrics.push({
      key: 'metrics.shopify_baseline_revenue',
      label: 'Baseline Revenue',
      type: 'currency',
    });
    metrics.push({
      key: 'metrics.shopify_baseline_plus_paid_brand_search_revenue',
      label: 'Baseline + Paid Brand Search Revenue',
      type: 'currency',
    });
    if (depVar?.includes('subscription')) {
      metrics.push({
        key: 'metrics.shopify_baseline_plus_subscription_plus_paid_brand_search_revenue',
        label: 'Baseline + Subscription + Paid Brand Search Revenue',
        type: 'currency',
      });
    }
    if (amazonEnabled) {
      if (depVar?.includes('subscription')) {
        metrics.push({
          key: 'metrics.shopify_plus_subscription_amazon_baseline_revenue',
          label: 'Baseline + Subscription + Paid Brand Search + Amazon Owned Revenue',
          type: 'currency',
        });
      } else {
        metrics.push({
          key: 'metrics.shopify_amazon_baseline_revenue',
          label: 'Baseline + Paid Brand Search + Amazon Owned Revenue',
          type: 'currency',
        });
      }
    }
  }

  return metrics;
}

// The 3rd Party Retail rollup (`wholesale_total_revenue`, every retailer
// sell-through feed) is a term of Total Business Revenue since AMA-138, so the
// table shows it as its own row — only for companies with a connected retailer
// feed, mirroring the connector-gated `wholesale` table. Same key and label as that table's
// rollup row, so the catalog keeps a single entry.
const THIRD_PARTY_RETAIL_REVENUE: MetricDefinition = {
  key: 'metrics.wholesale_total_revenue',
  label: '3rd Party Retail Revenue',
  type: 'currency',
};

export function getTotalBusinessRevenueMetrics(prefs: ResolvedKpiPrefs): MetricDefinition[] {
  const { amazonEnabled, isShopifyConnected, wholesaleEnabled } = prefs;
  const metrics: MetricDefinition[] = [];

  if (amazonEnabled) {
    metrics.push({
      key: 'metrics.amazon_total_revenue',
      label: 'Amazon Total Revenue',
      type: 'currency',
    });
  }

  if (isShopifyConnected) {
    metrics.push({
      key: 'metrics.shopify_total_customer_revenue',
      label: 'Shopify Customer Revenue Total',
      type: 'currency',
    });
    if (wholesaleEnabled) {
      metrics.push(THIRD_PARTY_RETAIL_REVENUE);
    }
    metrics.push({
      key: 'metrics.total_business_revenue_shopify',
      label: 'Total Business Revenue',
      type: 'currency',
    });
  } else {
    metrics.push({
      key: 'metrics.ga_multi_property_revenue',
      label: 'GA Total Revenue',
      type: 'currency',
    });
    if (wholesaleEnabled) {
      metrics.push(THIRD_PARTY_RETAIL_REVENUE);
    }
    metrics.push({
      key: 'metrics.total_business_revenue_ga',
      label: 'Total Business Revenue',
      type: 'currency',
    });
  }

  return metrics;
}

export function getGaRevenueMetrics(): MetricDefinition[] {
  return [
    {
      key: 'metrics.ga_multi_property_baseline_revenue',
      label: 'GA Baseline Revenue',
      type: 'currency',
    },
    {
      key: 'metrics.ga_multi_property_triggered_revenue',
      label: 'GA Triggered Revenue',
      type: 'currency',
    },
    {
      key: 'metrics.ga_multi_property_paid_revenue',
      label: 'GA Paid Revenue',
      type: 'currency',
    },
    {
      key: 'metrics.ga_multi_property_revenue',
      label: 'Total',
      type: 'currency',
    },
  ];
}
